import React from 'react'

import { PRODUCT_GLYPHS, type ProductGlyphName } from 'constants/purchaseFlowRevamp/productGlyphs'
import { ProductGlyphSvg } from './ProductGlyphSvg.component'

/**
 * Which glyph a product KIND gets.
 *
 * Keyed by kind because that is all a bundle member publishes — the wire's
 * `bundledProducts[]` carries `productCode` and `kind`, no group and no icon.
 * The entries are the kinds that actually appear as bundle members in the
 * catalogue (14 distinct across the active `BUNDLE_MANDATORY` relations), plus
 * the neighbouring kinds a non-bundle caller would ask for; it is not an attempt
 * to enumerate the enum.
 *
 * Several kinds share one glyph on purpose: the designer's set draws a SERVICE,
 * not a product tier, so every photo product is the camera and every video
 * product is the play button.
 */
const GLYPH_BY_KIND: Readonly<Record<string, ProductGlyphName>> = {
  // photography
  PHOTO: 'photo',
  GROUND_PHOTO: 'photo',
  GROUND_PHOTO_PRESTIGE: 'photo',
  EXTERIOR_SHOOT: 'photo',
  MATTERPORT_PHOTO: 'photo',
  MAST_PHOTO: 'photo',
  // virtual visit — `HD`/`SD` are the Matterport tour tiers
  HD: 'virtualVisit',
  SD: 'virtualVisit',
  // a 3D tour made with Floorfy, delivered as a share link
  FLOORFY_VIRTUAL_TOUR: 'virtualVisit',
  // floor plans, including the one derived from a Matterport scan
  MATTERPORT_FLOOR_PLAN_WITH_VISIT: 'floorPlan',
  MATTERPORT_FLOOR_PLAN_WITHOUT_VISIT: 'floorPlan',
  MOBILE_APPLICATION_FLOOR_PLAN: 'floorPlan',
  FLOOR_PLAN_CERTIFICATION: 'floorPlan',
  MEASUREMENT_ON_SITE: 'floorPlan',
  FLOOR_PLAN_RENDERING_3D: 'rendering',
  // editing, as the catalogue's editing groups draw it
  FLOOR_PLAN_EDITING_2D: 'editing',
  FLOOR_PLAN_EDITING_3D: 'editing',
  PHOTO_BLURRING: 'editing',
  AI_PHOTO_EDITING: 'aiEditing',
  // drone, including drone-only video, as its catalogue group draws it
  DRONE_PHOTO: 'drone',
  STANDARD_DRONE_VIDEO: 'drone',
  // video, including the social cut — it is a video deliverable, not a still
  TEASER_VIDEO: 'video',
  SLIDESHOW: 'video',
  STANDARD_GROUND_VIDEO: 'video',
  REEL_VIDEO: 'video',
  STANDARD_GROUND_AND_DRONE_VIDEO: 'video',
  PRESTIGE_GROUND_AND_DRONE_VIDEO: 'video',
  // staging and rendering
  RESIDENTIAL_STAGING: 'staging',
  COMMERCIAL_STAGING: 'staging',
  RESIDENTIAL_RENDERING: 'rendering',
  COMMERCIAL_RENDERING: 'rendering',
  // documents
  AUTHORITIES_DOCUMENTS: 'authoritiesDocument',
  ENERGY_CERTIFICATE: 'energyCertificate',
  CONSUMPTION_CERTIFICATE: 'energyCertificate',
  ENERGY_CERTIFICATE_OF_NEED: 'energyCertificate',
}

/**
 * The glyph for a kind, or `undefined` when none applies.
 *
 * `OTHER` is deliberately absent, and so is anything Salesforce adds later: a
 * glyph states what the service IS, so a wrong one is worse than none. The
 * caller renders nothing rather than falling back to a generic picture.
 */
export const glyphNameForKind = (kind: string | undefined): ProductGlyphName | undefined =>
  kind ? GLYPH_BY_KIND[kind] : undefined

interface Props {
  /** Product kind as the catalogue publishes it. */
  kind?: string
  /** Edge length. Defaults to the 16px the prototype uses inside a tag. */
  size?: string
}

/**
 * The bare glyph for one product kind — no disc, no padding — for use inside a
 * tag or a list row. `ProductGroupIcon` is the disc-on-a-card variant.
 *
 * @example
 * <ProductKindIcon kind="GROUND_PHOTO" />
 */
export const ProductKindIcon: React.FC<Props> = ({ kind, size = '1.6rem' }) => {
  const name = glyphNameForKind(kind)
  if (!name) return null

  return (
    <ProductGlyphSvg
      glyph={PRODUCT_GLYPHS[name]}
      sx={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    />
  )
}
